import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingUpdateRequestKind, type BookingUpdateOfferPublicRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiAsyncConfirmationDialog, NoddiBasicCard, noddiToast } from 'noddi-ui-web';
import { compareBookingUpdateDiscountTierThresholds, customerRoutes } from 'noddi-util';
import type { ComponentProps, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDeclineOfferUserFacingError, getTierDiscountLabel } from '../../../utils/bookingUpdateOfferSectionUtils';

interface Props {
  bookingSlug: string;
  offer: BookingUpdateOfferPublicRecord;
  onShowWidenFlow: () => void;
  onRequireLogin: (action: 'widen' | 'reschedule' | 'decline') => boolean;
  onSuccess: () => Promise<void>;
}

const ActionCard = ({
  description,
  iconName,
  onClick,
  title
}: {
  description: ReactNode;
  iconName: ComponentProps<typeof NoddiIcon>['name'];
  onClick?: () => void;
  title: ReactNode;
}) => (
  <NoddiBasicCard onClick={onClick}>
    <div className='flex items-center justify-between gap-3'>
      <div className='flex items-center gap-3'>
        <NoddiIcon name={iconName} />
        <div>
          <p className='font-semibold'>{title}</p>
          <p className='text-sm text-muted-foreground'>{description}</p>
        </div>
      </div>
      <NoddiIcon name='ChevronRight' className='text-muted-foreground' />
    </div>
  </NoddiBasicCard>
);

export const BookingUpdateOfferActions = ({
  bookingSlug,
  offer,
  onShowWidenFlow,
  onRequireLogin,
  onSuccess
}: Props) => {
  const navigate = useNavigate();

  const { mutate: declineOffer, isPending: isDeclinePending } = noddiAsync.usePost({
    type: URLKeys.postBookingUpdateOfferDecline,
    queryConfig: {
      onSuccess,
      onError: (error) => {
        noddiToast.error(getDeclineOfferUserFacingError(error) ?? t`Something went wrong. Please try again.`);
      }
    }
  });

  const handleDecline = async () => {
    if (onRequireLogin('decline')) {
      return;
    }

    declineOffer({ offerUid: offer.id });
  };

  const bestDiscountLabel =
    offer.discountTiers.length > 0
      ? getTierDiscountLabel([...offer.discountTiers].sort(compareBookingUpdateDiscountTierThresholds).at(-1)!)
      : null;

  return (
    <div className='flex flex-col gap-4'>
      <p className='text-sm text-foreground/80'>
        <Trans>Pick whichever works best for you.</Trans>
      </p>

      {offer.widenOffered ? (
        <ActionCard
          iconName='Clock'
          onClick={onShowWidenFlow}
          title={<Trans>I can change my time window on the same day</Trans>}
          description={
            bestDiscountLabel != null ? (
              <Trans>Get up to {bestDiscountLabel} off</Trans>
            ) : (
              <Trans>Helps us plan on your booking date</Trans>
            )
          }
        />
      ) : null}

      {offer.rescheduleOffered ? (
        <ActionCard
          iconName='Calendar'
          onClick={() => {
            if (onRequireLogin('reschedule')) {
              return;
            }
            navigate(customerRoutes.editMyBookingTimeWindow.getPath({ slug: bookingSlug }));
          }}
          title={<Trans>I can move my booking to another day</Trans>}
          description={<Trans>Pick a different date</Trans>}
        />
      ) : null}

      {offer.allowedCustomerActions.includes(BookingUpdateRequestKind.KEEP_AS_IS) ? (
        <NoddiAsyncConfirmationDialog
          title={t`Keep your current time?`}
          description={t`No problem at all! We'll keep your booking as planned.`}
          confirmText={t`Yes, keep my time`}
          cancelText={t`Back`}
          confirmAction={handleDecline}
          confirmActionLoading={isDeclinePending}
          trigger={
            <ActionCard
              iconName='Check'
              title={<Trans>I can't make changes</Trans>}
              description={<Trans>That's completely fine if you don't want to make changes</Trans>}
            />
          }
        />
      ) : null}
    </div>
  );
};
